/**
 * NotFoundPage — 404 catch-all.
 *
 * Used by the `path="*"` route in App.jsx for any URL that doesn't match
 * a known route. Renders inside whatever layout the route was mounted in
 * (PublicLayout for app-wide, or you can wrap with PrivateLayout for an
 * authenticated 404).
 *
 * Design language matches the refresh: brand chip, calm typography,
 * primary CTA back to dashboard, secondary CTA back to previous page.
 * Fully dark + RTL + i18n aware.
 */
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  HiOutlineArrowLeft,
  HiOutlineHome,
  HiOutlineMagnifyingGlass,
} from 'react-icons/hi2'
import { Button } from '../components/ui'
import ovacsIcon from '../assets/images/icons/Icon.svg'

export default function NotFoundPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full text-center">
        <div className="flex justify-center mb-6">
          <img src={ovacsIcon} alt="" aria-hidden="true" className="w-16 h-16 opacity-80" />
        </div>

        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-soft text-brand text-xs font-semibold uppercase tracking-wider mb-4">
          <HiOutlineMagnifyingGlass className="w-3.5 h-3.5" />
          {t('notFound.code', '404')}
        </div>

        <h1 className="text-3xl sm:text-4xl font-semibold text-ink-primary leading-tight mb-3">
          {t('notFound.title', 'Page not found')}
        </h1>
        <p className="text-sm sm:text-base text-ink-secondary mb-8 leading-relaxed">
          {t('notFound.body', "The page you're looking for doesn't exist or has been moved. Check the URL or head back to your dashboard.")}
        </p>

        <div className="flex flex-col sm:flex-row gap-2 justify-center">
          <Button
            variant="secondary"
            leadingIcon={HiOutlineArrowLeft}
            onClick={() => navigate(-1)}
            className="rtl:[&_svg]:rotate-180"
          >
            {t('common.back')}
          </Button>
          <Button
            leadingIcon={HiOutlineHome}
            onClick={() => navigate('/dashboard')}
          >
            {t('notFound.backToDashboard', 'Back to dashboard')}
          </Button>
        </div>
      </div>
    </div>
  )
}
